import {
  AlertCircle,
  ChevronRight,
  CircleDot,
  FileText,
  Play,
  Plus,
  Sparkles,
  Stethoscope,
} from "lucide-react";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";

import type { PaginatedEncounterListItem } from "@elation/api-client/src/stitch-web/models/paginatedEncounterListItem";

import { useTourGuide, VISIT_TOUR_ID } from "@src/features/tourguide";
import { ErrorMessage } from "@src/shared/components/general/ErrorMessage";
import {
  Flexbox,
  HorizontalFlexbox,
  VerticalFlexbox,
} from "@src/shared/components/layout/Flexbox";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@src/shared/components/ui/alert";
import { Avatar, AvatarFallback } from "@src/shared/components/ui/avatar";
import { Button } from "@src/shared/components/ui/button";
import { Skeleton } from "@src/shared/components/ui/skeleton";
import { navigationV2 } from "@src/shared/navigation/v2";

import { useRecentVisitsQuery } from "../../../../data";
import {
  EncounterEntity,
  type EncounterRecencyGroup,
} from "../../../../domain";

function VisitsListSkeleton() {
  return (
    <VerticalFlexbox
      gap="md"
      className="rounded-card border border-border bg-surface p-card shadow-soft"
    >
      <Skeleton className="h-3 w-20 rounded-full" />
      <VerticalFlexbox gap="md">
        {[0, 1, 2].map((i) => (
          <HorizontalFlexbox key={i} alignItems="center" className="gap-3">
            <Skeleton className="size-10 rounded-full" />
            <VerticalFlexbox gap="sm" className="min-w-0 flex-1">
              <Skeleton className="h-4 w-36" />
              <Skeleton className="h-3 w-full max-w-md" />
              <Skeleton className="h-3 w-32" />
            </VerticalFlexbox>
          </HorizontalFlexbox>
        ))}
      </VerticalFlexbox>
    </VerticalFlexbox>
  );
}

const EMPTY_VISITS_STEPS = [
  { label: "Prep the agenda", Icon: FileText },
  { label: "Run the visit", Icon: CircleDot },
  { label: "Walk out with codes", Icon: Sparkles },
];

function EmptyVisitsList() {
  const navigate = useNavigate();
  const visitTour = useTourGuide(VISIT_TOUR_ID);
  const startVisitTour = () => visitTour.startTour();

  return (
    <VerticalFlexbox
      alignItems="center"
      justifyContent="center"
      className="min-h-[560px] flex-1 px-[30px] py-12 text-center"
    >
      <VerticalFlexbox alignItems="center" className="w-full max-w-[400px]">
        <Flexbox
          alignItems="center"
          justifyContent="center"
          className="mb-5 size-12 rounded-[13px] bg-primary/5 text-primary"
        >
          <Stethoscope className="size-[23px]" aria-hidden="true" />
        </Flexbox>
        <h2 className="max-w-[320px] text-balance text-[27px] font-semibold leading-[1.18] tracking-[-0.026em] text-surface-foreground">
          Get credit for the
          <br />
          care you already give
        </h2>
        <p className="mt-[11px] max-w-[380px] text-balance text-sm leading-[1.6] text-muted-foreground">
          Your care gaps become the agenda. The conversation becomes the note.
          The codes come back ready for your chart, coded to the specificity
          payers look for.
        </p>

        <VerticalFlexbox
          alignItems="flex-start"
          className="mb-[30px] mt-7 gap-[11px]"
        >
          {EMPTY_VISITS_STEPS.map(({ label, Icon }) => (
            <HorizontalFlexbox
              key={label}
              alignItems="center"
              className="gap-2.5 text-sm font-semibold text-surface-foreground"
            >
              <Flexbox
                alignItems="center"
                justifyContent="center"
                className="size-[26px] shrink-0 rounded-full bg-primary/5 text-primary"
              >
                <Icon className="size-3.5 stroke-[2.2]" aria-hidden="true" />
              </Flexbox>
              <span>{label}</span>
            </HorizontalFlexbox>
          ))}
        </VerticalFlexbox>

        <VerticalFlexbox className="w-full max-w-[320px] gap-2.5">
          <div className="rounded-[9px] shadow-[0_0_18px_rgba(14,143,158,0.55)]">
            <Button
              type="button"
              variant="outline"
              className="h-[34px] w-full rounded-[9px] border-border bg-background text-[13.5px] font-semibold text-surface-foreground shadow-none hover:bg-accent"
              onClick={startVisitTour}
            >
              <Sparkles className="size-4 stroke-[2.2]" aria-hidden="true" />
              Go through a test visit
            </Button>
          </div>
          <Button
            type="button"
            className="h-[34px] w-full rounded-[9px] text-[13.5px] font-semibold shadow-none"
            onClick={() => navigate(navigationV2.newVisit())}
          >
            <Plus className="size-4 stroke-[2.2]" aria-hidden="true" />
            Start a visit
          </Button>
        </VerticalFlexbox>

        <Button
          type="button"
          variant="ghost"
          className="mt-3.5 h-auto gap-1.5 px-0 py-0 text-[12.5px] font-semibold text-primary hover:bg-transparent hover:text-primary/80"
          onClick={() => navigate(navigationV2.newVisit())}
        >
          <Play className="size-[13px] stroke-[2.2]" aria-hidden="true" />
          New here? Watch a 30-second demo
        </Button>
      </VerticalFlexbox>
    </VerticalFlexbox>
  );
}

function ErrorVisitsList({ error }: { error: unknown }) {
  return (
    <Alert
      variant="destructive"
      className="rounded-card border-destructive/40 bg-destructive/5 p-card"
    >
      <AlertCircle className="size-4" aria-hidden="true" />
      <AlertTitle>Unable to load visits</AlertTitle>
      <ErrorMessage
        as={AlertDescription}
        error={error}
        fallback="Something went wrong retrieving recent visits."
      />
    </Alert>
  );
}

interface VisitRowProps {
  item: PaginatedEncounterListItem;
  groupId: EncounterRecencyGroup["id"];
}

function VisitRowContent({
  item,
  groupId,
}: Pick<VisitRowProps, "item" | "groupId">) {
  const patientName = EncounterEntity.patientDisplayName(item);
  return (
    <>
      <Avatar className="size-10 border border-border bg-background">
        <AvatarFallback className="bg-primary/10 font-mono text-[11px] font-semibold text-primary">
          {EncounterEntity.patientInitials(item)}
        </AvatarFallback>
      </Avatar>
      <VerticalFlexbox className="min-w-0 flex-1 text-left">
        <HorizontalFlexbox
          alignItems="center"
          flexWrap="wrap"
          className="gap-x-2 gap-y-1"
        >
          <span className="truncate text-sm font-semibold text-surface-foreground">
            {patientName || "Unknown patient"}
          </span>
        </HorizontalFlexbox>
        <p className="mt-1 line-clamp-2 text-sm leading-5 text-muted-foreground md-stitch:truncate">
          {item.notePreview || "No note preview yet."}
        </p>
        <p className="mt-1.5 font-mono text-[10.5px] text-muted-foreground">
          {EncounterEntity.listMetadata(item, groupId)}
        </p>
      </VerticalFlexbox>
      <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
    </>
  );
}

function VisitRow({ item, groupId }: VisitRowProps) {
  const navigate = useNavigate();

  return (
    <Button
      type="button"
      variant="ghost"
      className="h-auto w-full justify-start gap-[11px] whitespace-normal rounded-none border-b border-border bg-transparent px-[18px] py-[13px] text-left font-normal shadow-none hover:bg-muted/40 hover:text-foreground focus-visible:ring-1"
      onClick={() => navigate(navigationV2.visit(item.id))}
    >
      <VisitRowContent item={item} groupId={groupId} />
    </Button>
  );
}

export function VisitsListV2() {
  const navigate = useNavigate();
  const query = useRecentVisitsQuery();
  const groups = useMemo(
    () => EncounterEntity.groupByRecency(query.data?.data.items ?? []),
    [query.data],
  );

  if (query.isLoading) return <VisitsListSkeleton />;
  if (query.isError) return <ErrorVisitsList error={query.error} />;
  if (groups.length === 0) return <EmptyVisitsList />;

  return (
    <VerticalFlexbox>
      <Flexbox className="px-[18px] pb-3 pt-4">
        <Button
          type="button"
          className="w-full"
          onClick={() => navigate(navigationV2.newVisit())}
        >
          <Plus className="size-4" aria-hidden="true" />
          Start a visit
        </Button>
      </Flexbox>
      {groups.map((group) => (
        <section key={group.id} aria-labelledby={`visits-${group.id}-heading`}>
          <h2
            id={`visits-${group.id}-heading`}
            className="px-[18px] pb-1 pt-2 font-mono text-[10px] font-medium uppercase tracking-[0.06em] text-muted-foreground"
          >
            {group.label}
          </h2>
          <VerticalFlexbox>
            {group.items.map((item) => (
              <VisitRow key={item.id} item={item} groupId={group.id} />
            ))}
          </VerticalFlexbox>
        </section>
      ))}
    </VerticalFlexbox>
  );
}
